import {
  addUserFont,
  ensureLoaded,
  getAllFonts,
  removeUserFont,
  subscribeFonts,
  type FontEntry,
} from './registry';
import { GOOGLE_KOREAN, GOOGLE_LATIN, NOONNU_FONTS, type CuratedFont } from './curated';

type Tab = 'installed' | 'google' | 'noonnu' | 'add';

export class FontModal {
  private bg!: HTMLElement;
  private body!: HTMLElement;
  private tabBar!: HTMLElement;
  private statusBar!: HTMLElement;
  private tab: Tab = 'installed';
  private searchTerm = '';
  private onSelect: (family: string) => void;

  constructor(onSelect: (family: string) => void) {
    this.onSelect = onSelect;
    this.build();
    subscribeFonts(() => {
      if (this.bg.classList.contains('show')) this.renderTab();
    });
  }

  private build(): void {
    this.bg = document.createElement('div');
    this.bg.className = 'modal-bg';
    const modal = document.createElement('div');
    modal.className = 'modal modal-lg';

    const h = document.createElement('h3');
    h.textContent = '🅰 폰트';
    const desc = document.createElement('p');
    desc.textContent =
      '클릭해서 선택. 모두 외부 CDN 또는 너의 브라우저에서 로드 — 우리 서버 거치지 않음.';

    this.tabBar = document.createElement('div');
    this.tabBar.className = 'tab-bar';
    const tabs: [Tab, string][] = [
      ['installed', '설치됨'],
      ['google', 'Google Fonts'],
      ['noonnu', '누누폰트'],
      ['add', '직접 추가'],
    ];
    for (const [id, label] of tabs) {
      const b = document.createElement('button');
      b.className = 'btn ghost';
      b.textContent = label;
      b.dataset.tab = id;
      b.addEventListener('click', () => this.setTab(id));
      this.tabBar.appendChild(b);
    }

    this.body = document.createElement('div');
    this.body.className = 'font-body';

    this.statusBar = document.createElement('div');
    this.statusBar.style.fontSize = '11px';
    this.statusBar.style.color = 'var(--ink-dim)';
    this.statusBar.style.minHeight = '18px';
    this.statusBar.style.marginTop = '6px';

    const actions = document.createElement('div');
    actions.className = 'row-actions';
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn';
    closeBtn.textContent = '닫기';
    closeBtn.addEventListener('click', () => this.close());
    actions.appendChild(closeBtn);

    modal.append(h, desc, this.tabBar, this.body, this.statusBar, actions);
    this.bg.appendChild(modal);
    this.bg.addEventListener('click', (e) => {
      if (e.target === this.bg) this.close();
    });
    document.body.appendChild(this.bg);
  }

  open(): void {
    this.bg.classList.add('show');
    this.setTab(this.tab);
  }
  close(): void {
    this.bg.classList.remove('show');
  }

  private setTab(tab: Tab): void {
    this.tab = tab;
    this.searchTerm = '';
    this.tabBar.querySelectorAll<HTMLElement>('button').forEach((b) => {
      b.classList.toggle('active', b.dataset.tab === tab);
    });
    this.renderTab();
  }

  private renderTab(): void {
    this.body.replaceChildren();

    if (this.tab === 'add') {
      this.renderAddTab();
      return;
    }

    const search = document.createElement('input');
    search.type = 'text';
    search.placeholder = '검색…';
    search.value = this.searchTerm;
    search.addEventListener('input', () => {
      this.searchTerm = search.value.toLowerCase();
      this.renderList();
    });
    const list = document.createElement('div');
    list.className = 'font-list';
    list.id = 'font-list';
    this.body.append(search, list);

    this.renderList();
  }

  private renderList(): void {
    const list = this.body.querySelector('#font-list') as HTMLElement | null;
    if (!list) return;
    list.replaceChildren();

    let items: Array<FontEntry | CuratedFont> = [];
    if (this.tab === 'installed') {
      const all = getAllFonts();
      items = [...all.builtin, ...all.user];
    } else if (this.tab === 'google') {
      items = [...GOOGLE_KOREAN, ...GOOGLE_LATIN];
    } else if (this.tab === 'noonnu') {
      items = NOONNU_FONTS;
    }

    const filtered = this.searchTerm
      ? items.filter((f) => f.family.toLowerCase().includes(this.searchTerm))
      : items;

    if (filtered.length === 0) {
      const empty = document.createElement('div');
      empty.style.color = 'var(--ink-dim)';
      empty.style.padding = '16px';
      empty.textContent = '검색 결과 없음';
      list.appendChild(empty);
      return;
    }

    for (const item of filtered) {
      const row = document.createElement('div');
      row.className = 'font-row';

      const preview = document.createElement('div');
      preview.className = 'font-preview';
      preview.style.fontFamily = `"${item.family}", sans-serif`;
      preview.textContent = item.family;

      const sub = document.createElement('div');
      sub.className = 'font-sub';
      const hint = (item as FontEntry).hint ?? (item as CuratedFont).hint ?? '';
      const cat = (item as FontEntry).category ?? (item as CuratedFont).category ?? '';
      sub.textContent = [item.family, cat, hint].filter(Boolean).join(' · ');

      const btnUse = document.createElement('button');
      btnUse.className = 'btn primary';
      btnUse.textContent = '사용';
      btnUse.addEventListener('click', () => this.useFont(item));

      // Pre-load preview font so it actually renders in the preview text
      ensurePreviewLoaded(item);

      row.append(preview, sub, btnUse);

      if (this.tab === 'installed' && (item as FontEntry).source !== 'builtin') {
        const del = document.createElement('button');
        del.className = 'btn ghost';
        del.textContent = '✕';
        del.addEventListener('click', (e) => {
          e.stopPropagation();
          removeUserFont(item.family);
        });
        row.appendChild(del);
      }

      list.appendChild(row);
    }
  }

  private renderAddTab(): void {
    const intro = document.createElement('p');
    intro.style.color = 'var(--ink-mute)';
    intro.style.fontSize = '12px';
    intro.textContent =
      'CSS URL이나 폰트 파일을 직접 추가할 수 있어. 누누폰트 페이지에서 가져온 webfont URL이나 본인이 만든 .ttf/.otf/.woff2 파일도 OK.';

    // CSS URL form
    const cssWrap = document.createElement('div');
    cssWrap.className = 'add-form';
    const cssTitle = document.createElement('h4');
    cssTitle.textContent = 'CSS URL로 추가';
    cssTitle.className = 'form-title';

    const cssFamily = mkInput('text', '폰트 패밀리 이름 (예: My Korean Font)');
    const cssUrl = mkInput('text', 'https://… 의 .css 또는 Google Fonts URL');
    const cssAdd = document.createElement('button');
    cssAdd.className = 'btn primary';
    cssAdd.textContent = '추가';
    cssAdd.addEventListener('click', () => {
      if (!cssFamily.value.trim() || !cssUrl.value.trim()) {
        this.fail('패밀리와 URL을 모두 입력');
        return;
      }
      addUserFont({
        family: cssFamily.value.trim(),
        source: 'cdn',
        cssUrl: cssUrl.value.trim(),
      });
      this.success(`추가됨: ${cssFamily.value.trim()}`);
      cssFamily.value = '';
      cssUrl.value = '';
    });
    cssWrap.append(cssTitle, cssFamily, cssUrl, cssAdd);

    // Direct font file URL
    const fontUrlWrap = document.createElement('div');
    fontUrlWrap.className = 'add-form';
    const fontUrlTitle = document.createElement('h4');
    fontUrlTitle.textContent = '폰트 파일 URL (.ttf / .otf / .woff2)';
    fontUrlTitle.className = 'form-title';
    const fontUrlFamily = mkInput('text', '폰트 패밀리 이름');
    const fontUrlInput = mkInput('text', 'https://…/font.woff2');
    const fontUrlAdd = document.createElement('button');
    fontUrlAdd.className = 'btn primary';
    fontUrlAdd.textContent = '추가';
    fontUrlAdd.addEventListener('click', () => {
      if (!fontUrlFamily.value.trim() || !fontUrlInput.value.trim()) {
        this.fail('패밀리와 URL을 모두 입력');
        return;
      }
      addUserFont({
        family: fontUrlFamily.value.trim(),
        source: 'cdn',
        fontUrl: fontUrlInput.value.trim(),
      });
      this.success(`추가됨: ${fontUrlFamily.value.trim()}`);
      fontUrlFamily.value = '';
      fontUrlInput.value = '';
    });
    fontUrlWrap.append(fontUrlTitle, fontUrlFamily, fontUrlInput, fontUrlAdd);

    // File upload
    const upWrap = document.createElement('div');
    upWrap.className = 'add-form';
    const upTitle = document.createElement('h4');
    upTitle.textContent = '폰트 파일 업로드';
    upTitle.className = 'form-title';
    const upFamily = mkInput('text', '폰트 패밀리 이름');
    const upPicker = document.createElement('input');
    upPicker.type = 'file';
    upPicker.accept = '.ttf,.otf,.woff,.woff2';
    const upAdd = document.createElement('button');
    upAdd.className = 'btn primary';
    upAdd.textContent = '업로드';
    upAdd.addEventListener('click', async () => {
      const family = upFamily.value.trim();
      const file = upPicker.files?.[0];
      if (!family || !file) {
        this.fail('패밀리 이름과 파일 모두 필요');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        addUserFont({ family, source: 'upload', dataUrl });
        this.success(`업로드됨: ${family} (${Math.round(file.size / 1024)} KB)`);
        upFamily.value = '';
        upPicker.value = '';
      };
      reader.onerror = () => this.fail('파일 로드 실패');
      reader.readAsDataURL(file);
    });
    upWrap.append(upTitle, upFamily, upPicker, upAdd);

    this.body.append(intro, cssWrap, fontUrlWrap, upWrap);
  }

  private useFont(item: FontEntry | CuratedFont): void {
    if ('source' in item) {
      ensureLoaded(item);
    } else {
      // curated → register as user font so it persists
      const entry: FontEntry = {
        family: item.family,
        source: this.tab === 'noonnu' ? 'noonnu' : 'google',
        ...(item.cssUrl.endsWith('.css') ||
        item.cssUrl.includes('googleapis.com') ||
        item.cssUrl.endsWith('/static/pretendard.css')
          ? { cssUrl: item.cssUrl }
          : { fontUrl: item.cssUrl }),
        ...(item.category ? { category: item.category as FontEntry['category'] } : {}),
        ...(item.hint ? { hint: item.hint } : {}),
      };
      addUserFont(entry);
    }
    this.onSelect(item.family);
    this.success(`적용됨: ${item.family}`);
  }

  private fail(msg: string): void {
    this.statusBar.textContent = `⚠ ${msg}`;
    this.statusBar.style.color = 'var(--accent)';
  }
  private success(msg: string): void {
    this.statusBar.textContent = `✅ ${msg}`;
    this.statusBar.style.color = 'var(--ok)';
  }
}

function ensurePreviewLoaded(item: FontEntry | CuratedFont): void {
  if ('source' in item) {
    ensureLoaded(item);
  } else if (item.cssUrl.endsWith('.css') || item.cssUrl.includes('googleapis.com')) {
    ensureLoaded({ family: item.family, source: 'google', cssUrl: item.cssUrl });
  } else {
    ensureLoaded({ family: item.family, source: 'noonnu', fontUrl: item.cssUrl });
  }
}

function mkInput(type: 'text', placeholder: string): HTMLInputElement {
  const i = document.createElement('input');
  i.type = type;
  i.placeholder = placeholder;
  return i;
}
